import { useState, useEffect, useRef } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Radio, MapPin, CheckCircle, XCircle, AlertCircle, Signal, Wifi, Loader2, Radar, ChevronDown, Lock, ArrowRight } from 'lucide-react'
import { useMutation } from '@tanstack/react-query'
import ReCAPTCHA from 'react-google-recaptcha'
import { checkNetworkAll, getBestTechnology, getSignalQuality, NetworkCheckResult } from '@/services/networkCoverage'
import { Card } from '@/components/ui/Card'
import { NETWORK_INFO, NetworkKey } from '@/data/networkCoverageInfo'
import { useAuthStore } from '@/stores/authStore'
import { usePortal } from '@/contexts/PortalContext'
import { useRecaptchaSiteKey } from '@/hooks/useRecaptchaSiteKey'
import CoverageNextSteps from '@/components/CoverageNextSteps'

type InfoBlockKey = 'networks' | 'coverage' | 'bands' | 'status'

export default function NetworkCoverage() {
  const [searchParams] = useSearchParams()
  // Also served on white-label portals, where the schema below must name the RESELLER —
  // publishing "Silux Mobile" as the provider on their domain would be plainly wrong.
  const { portal } = usePortal()
  const [postcode, setPostcode] = useState('')
  const [result, setResult] = useState<NetworkCheckResult | null>(null)
  const [recaptchaError, setRecaptchaError] = useState(false)
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null)
  const recaptchaRef = useRef<ReCAPTCHA>(null)
  // Domain-aware: the main-site key cannot render on a reseller domain.
  const recaptchaSiteKey = useRecaptchaSiteKey()
  const [expandedBlock, setExpandedBlock] = useState<InfoBlockKey | null>(null)
  const [activeNetwork, setActiveNetwork] = useState<NetworkKey>('three')

  const checkMutation = useMutation({
    mutationFn: ({ postcode, token }: { postcode: string; token: string }) =>
      checkNetworkAll(postcode, token),
    onSuccess: (data) => {
      setResult(data)
    },
  })

  // Pre-fill postcode from URL
  useEffect(() => {
    const urlPostcode = searchParams.get('postcode')
    if (urlPostcode) {
      setPostcode(urlPostcode.toUpperCase())
    }
  }, [searchParams])

  // Format postcode to proper UK format (e.g., "S433QE" -> "S43 3QE")
  const formatPostcode = (pc: string): string => {
    const clean = pc.replace(/\s+/g, '').toUpperCase()
    if (clean.length >= 5 && clean.length <= 7) {
      return clean.slice(0, -3) + ' ' + clean.slice(-3)
    }
    return clean
  }

  const runCheck = () => {
    if (!postcode.trim()) return
    setRecaptchaError(false)

    if (recaptchaSiteKey && !recaptchaToken) {
      setRecaptchaError(true)
      return
    }

    const token = recaptchaToken
    recaptchaRef.current?.reset()
    setRecaptchaToken(null)

    setResult(null)
    checkMutation.reset()
    const formattedPostcode = formatPostcode(postcode.trim())
    setPostcode(formattedPostcode)
    checkMutation.mutate({ postcode: formattedPostcode, token: token || '' })
  }

  const getNetworkGradient = (network: string) => {
    switch (network) {
      case 'three': return 'from-primary-500 to-pink-500'
      case 'vodafone': return 'from-red-500 to-rose-500'
      case 'o2': return 'from-blue-500 to-primary-500'
      case 'ee': return 'from-teal-500 to-cyan-500'
      default: return 'from-gray-500 to-gray-600'
    }
  }

  const getNetworkName = (network: string) => {
    switch (network) {
      case 'three': return 'Three'
      case 'vodafone': return 'Vodafone'
      case 'o2': return 'O2'
      case 'ee': return 'EE'
      default: return network
    }
  }

  const getSignalBars = (quality: string) => {
    const levels = { excellent: 5, good: 4, fair: 3, poor: 2, none: 0 }
    const level = levels[quality as keyof typeof levels] || 0
    return (
      <div className="flex items-end gap-0.5 h-5">
        {[1, 2, 3, 4, 5].map((bar) => (
          <div
            key={bar}
            className={`w-1.5 rounded-sm transition-all ${
              bar <= level
                ? bar <= 2 ? 'bg-red-500' : bar <= 3 ? 'bg-yellow-500' : 'bg-green-500'
                : 'bg-gray-300'
            }`}
            style={{ height: `${bar * 4}px` }}
          />
        ))}
      </div>
    )
  }

  const brandName = portal ? (portal.trading_name || portal.company_name || 'Mobile') : 'Silux Mobile'
  const siteUrl = portal ? window.location.origin : 'https://www.siluxmobile.co.uk'

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'UK Mobile Network Coverage Checker',
    description: 'Postcode-level 4G and 5G coverage check across EE, O2, Three and Vodafone, with comparative town coverage and frequency band data for each major UK mobile network.',
    provider: { '@type': 'Organization', name: brandName, url: siteUrl },
    serviceType: 'Mobile Network Coverage Checker',
    areaServed: 'United Kingdom',
  }

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
      { '@type': 'ListItem', position: 2, name: 'Network Coverage', item: `${siteUrl}/network-coverage` },
    ],
  }

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-primary-50 to-pink-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-primary-600 via-primary-500 to-pink-600 text-white">
        <div className="absolute inset-0 bg-grid-white/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="text-center">
            <div className="inline-flex p-4 bg-white/20 backdrop-blur-sm rounded-2xl mb-6">
              <Radio className="w-12 h-12 sm:w-16 sm:h-16" />
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4 sm:mb-6">
              Network Coverage Checker
            </h1>
            <p className="text-xl sm:text-2xl text-primary-100 mb-8 max-w-3xl mx-auto">
              Check 4G and 5G coverage from all UK networks in your area
            </p>

            {/* Coverage Check Form */}
            <div className="max-w-xl mx-auto">
              <div className="flex flex-col sm:flex-row gap-3 mb-3">
                <div className="relative flex-1">
                  <MapPin className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={postcode}
                    onChange={(e) => setPostcode(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === 'Enter' && runCheck()}
                    placeholder="Enter your postcode (e.g. SW1A 1AA)"
                    className="w-full pl-12 pr-4 py-4 bg-white/10 backdrop-blur-sm border-2 border-white/30 rounded-xl text-white placeholder-white/60 focus:border-white focus:ring-2 focus:ring-white/20 transition-all text-lg font-mono"
                    maxLength={10}
                  />
                </div>
                <button
                  onClick={runCheck}
                  disabled={checkMutation.isPending || !postcode.trim() || (!!recaptchaSiteKey && !recaptchaToken)}
                  className="px-8 py-4 bg-white text-primary-600 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {checkMutation.isPending ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Checking...
                    </>
                  ) : (
                    <>
                      <Signal className="w-5 h-5" />
                      Check Coverage
                    </>
                  )}
                </button>
              </div>
              {recaptchaSiteKey && (
                <div className="flex justify-center">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={recaptchaSiteKey}
                    onChange={setRecaptchaToken}
                    onExpired={() => setRecaptchaToken(null)}
                  />
                </div>
              )}
              {recaptchaError && (
                <p className="mt-2 text-sm text-red-300 text-center">Please complete the security check first.</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        {/* Loading State */}
        {checkMutation.isPending && (
          <Card className="max-w-3xl mx-auto bg-gradient-to-br from-primary-50 to-primary-50 border-primary-200 mb-12">
            <div className="flex items-center gap-4 py-8 justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
              <div>
                <p className="font-semibold text-primary-900">Checking coverage from all networks...</p>
                <p className="text-sm text-primary-600">This may take up to 60 seconds</p>
              </div>
            </div>
          </Card>
        )}

        {/* Error State */}
        {checkMutation.isError && (
          <Card className="max-w-3xl mx-auto border-l-4 border-l-red-500 bg-red-50/50 mb-12">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-red-100 rounded-full">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-red-900 mb-1">Coverage Check Failed</h3>
                <p className="text-red-700">
                  {(checkMutation.error as any)?.response?.data?.detail || 'Unable to check coverage. Please verify your postcode and try again.'}
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Results */}
        {result && !checkMutation.isPending && (() => {
          // Check if all networks have errors
          const allNetworksHaveErrors = Object.values(result.coverage).every(c => c.error)
          return (
          <div className="space-y-8 mb-12">
            {/* Results Header */}
            <div className="text-center">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Coverage Results</h2>
              <p className="text-gray-600">
                Showing coverage for <span className="font-mono font-semibold bg-gray-100 px-2 py-1 rounded">{result.postcode}</span>
              </p>
            </div>

            {/* Warning if all networks failed */}
            {allNetworksHaveErrors && (
              <Card className="max-w-3xl mx-auto border-l-4 border-l-yellow-500 bg-yellow-50/50">
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-yellow-100 rounded-full">
                    <AlertCircle className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-yellow-900 mb-1">Coverage Data Temporarily Unavailable</h3>
                    <p className="text-yellow-700 text-sm">
                      We couldn't retrieve coverage data from the network providers at this time.
                      This is usually temporary - please try again in a few minutes.
                    </p>
                  </div>
                </div>
              </Card>
            )}

            {/* Network Cards Grid - only show if at least some data is available */}
            {!allNetworksHaveErrors && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {Object.entries(result.coverage)
              .map(([network, coverage]) => {
                const bestTech = getBestTechnology(coverage)
                const quality = getSignalQuality(coverage)
                const hasError = coverage.error

                return (
                  <Card
                    key={network}
                    className={`overflow-hidden hover:shadow-xl transition-all transform hover:-translate-y-1 ${
                      hasError ? 'border-yellow-300 bg-yellow-50/30' : ''
                    }`}
                  >
                    {/* Network Header */}
                    <div className={`bg-gradient-to-r ${getNetworkGradient(network)} px-5 py-4 -mx-5 -mt-5 mb-4`}>
                      <div className="flex items-center justify-between text-white">
                        <span className="font-bold text-xl">{getNetworkName(network)}</span>
                        {!hasError && (
                          <span className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-medium">
                            {bestTech}
                          </span>
                        )}
                      </div>
                    </div>

                    {hasError ? (
                      <div className="flex items-center gap-3 py-4 text-yellow-700">
                        <AlertCircle className="w-5 h-5" />
                        <span>Data unavailable</span>
                      </div>
                    ) : (
                      <>
                        {/* Signal Strength */}
                        <div className="flex items-center justify-between mb-4">
                          <span className="text-gray-600 text-sm">Signal Quality</span>
                          <div className="flex items-center gap-2">
                            {getSignalBars(quality)}
                            <span className="text-sm font-medium capitalize text-gray-700">{quality}</span>
                          </div>
                        </div>

                        {/* Technology Support */}
                        <div className="grid grid-cols-2 gap-2">
                          <div className={`text-center py-2 rounded-lg ${coverage.has_5g ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                            <div className="font-bold text-sm">5G</div>
                            {coverage.has_5g ? <CheckCircle className="w-4 h-4 mx-auto" /> : <XCircle className="w-4 h-4 mx-auto" />}
                          </div>
                          <div className={`text-center py-2 rounded-lg ${coverage.has_4g ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                            <div className="font-bold text-sm">4G</div>
                            {coverage.has_4g ? <CheckCircle className="w-4 h-4 mx-auto" /> : <XCircle className="w-4 h-4 mx-auto" />}
                          </div>
                        </div>
                      </>
                    )}
                  </Card>
                )
              })}
            </div>
            )}

            {/* Next step: order, or have the result sent (Sales Strategy, owner 2026-10-15). */}
            {!allNetworksHaveErrors && <CoverageNextSteps result={result} isPortal={!!portal} />}

            {/* Check Another Button */}
            <div className="text-center">
              <button
                onClick={() => {
                  setResult(null)
                  setPostcode('')
                }}
                className="text-primary-600 hover:text-primary-800 font-medium"
              >
                Check another postcode
              </button>
            </div>
          </div>
          )
        })()}

        {/* Features Section - shown when no results */}
        {!result && !checkMutation.isPending && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <InfoCard
                icon={<Signal className="w-6 h-6 text-primary-500" />}
                iconBg="bg-primary-100"
                title="All UK Networks"
                body="Check coverage from Three, Vodafone, EE and O2 all in one place."
                active={expandedBlock === 'networks'}
                onClick={() => setExpandedBlock(b => b === 'networks' ? null : 'networks')}
              />
              <InfoCard
                icon={<Wifi className="w-6 h-6 text-blue-600" />}
                iconBg="bg-blue-100"
                title="4G & 5G Coverage"
                body="See which networks have 5G in your town and where 4G is available."
                active={expandedBlock === 'coverage'}
                onClick={() => setExpandedBlock(b => b === 'coverage' ? null : 'coverage')}
              />
              <InfoCard
                icon={<Radar className="w-6 h-6 text-purple-600" />}
                iconBg="bg-purple-100"
                title="Frequency Bands"
                body="Browse the 4G and 5G frequency bands each UK network uses."
                active={expandedBlock === 'bands'}
                onClick={() => setExpandedBlock(b => b === 'bands' ? null : 'bands')}
              />
              <InfoCard
                icon={<CheckCircle className="w-6 h-6 text-green-600" />}
                iconBg="bg-green-100"
                title="Real-Time Status"
                body="Check for any network outages or issues affecting your area."
                active={expandedBlock === 'status'}
                onClick={() => setExpandedBlock(b => b === 'status' ? null : 'status')}
              />
            </div>

            {expandedBlock && (
              <div className="mt-8">
                <Card className="backdrop-blur-sm bg-white/90 p-6 sm:p-8">
                  {expandedBlock !== 'status' && expandedBlock !== 'coverage' && (
                    <NetworkTabs active={activeNetwork} onChange={setActiveNetwork} />
                  )}

                  <div className={expandedBlock !== 'status' && expandedBlock !== 'coverage' ? 'mt-6' : ''}>
                    {expandedBlock === 'networks' && <NetworksPanel networkKey={activeNetwork} />}
                    {expandedBlock === 'coverage' && <CoveragePanel />}
                    {expandedBlock === 'bands' && <BandsPanel networkKey={activeNetwork} />}
                    {expandedBlock === 'status' && <StatusPanel />}
                  </div>
                </Card>
              </div>
            )}
          </>
        )}
      </div>
    </div>
    </>
  )
}

function InfoCard({
  icon, iconBg, title, body, active, onClick,
}: {
  icon: React.ReactNode
  iconBg: string
  title: string
  body: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={active}
      className={`text-left backdrop-blur-sm rounded-2xl p-6 transition-all border-2 ${
        active
          ? 'bg-white border-primary-400 shadow-xl -translate-y-1'
          : 'bg-white/90 border-transparent hover:shadow-lg hover:-translate-y-0.5'
      }`}
    >
      <div className="flex items-start justify-between">
        <div className={`inline-flex p-3 rounded-xl mb-4 ${iconBg}`}>
          {icon}
        </div>
        <ChevronDown
          className={`w-5 h-5 text-gray-400 transition-transform ${active ? 'rotate-180 text-primary-600' : ''}`}
        />
      </div>
      <h3 className="font-bold text-xl text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600 text-sm">{body}</p>
      <p className="mt-3 text-xs font-medium text-primary-600">
        {active ? 'Hide Details' : 'Click to See Details'}
      </p>
    </button>
  )
}

function getNetwork(key: NetworkKey) {
  return NETWORK_INFO.find(n => n.key === key)!
}

function NetworkTabs({
  active, onChange,
}: { active: NetworkKey; onChange: (k: NetworkKey) => void }) {
  const gradient = (key: NetworkKey) => {
    switch (key) {
      case 'three': return 'from-primary-500 to-pink-500'
      case 'vodafone': return 'from-red-500 to-rose-500'
      case 'o2': return 'from-blue-500 to-primary-500'
      case 'ee': return 'from-teal-500 to-cyan-500'
    }
  }
  return (
    <div className="flex flex-wrap gap-2">
      {NETWORK_INFO.map(n => {
        const isActive = n.key === active
        return (
          <button
            key={n.key}
            type="button"
            onClick={() => onChange(n.key)}
            className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${
              isActive
                ? `bg-gradient-to-r ${gradient(n.key)} text-white shadow-md`
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {n.name}
          </button>
        )
      })}
    </div>
  )
}

function NetworksPanel({ networkKey }: { networkKey: NetworkKey }) {
  const n = getNetwork(networkKey)
  return (
    <div>
      <h4 className="text-2xl font-bold text-gray-900 mb-4">{n.name} Coverage</h4>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5">
          <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">{n.name} 5G Coverage</p>

          <p className="text-lg font-semibold text-gray-900">{n.pop5g}</p>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5">
          <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">{n.name} 4G Coverage</p>
          <p className="text-lg font-semibold text-gray-900">{n.pop4g}</p>
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-5">
        <p className="text-sm font-bold text-gray-900 mb-3">Network Speeds (Average Download Speeds)</p>
        <ul className="space-y-1 text-gray-700">
          <li><span className="font-semibold">5G —</span> {n.speed5g}</li>
          <li><span className="font-semibold">4G —</span> {n.speed4g}</li>
        </ul>
      </div>
    </div>
  )
}

function CoveragePanel() {
  const [filter, setFilter] = useState('')
  const order: NetworkKey[] = ['ee', 'o2', 'three', 'vodafone']
  const orderedNetworks = order.map(k => NETWORK_INFO.find(n => n.key === k)!)

  const allCities = Array.from(
    new Set(NETWORK_INFO.flatMap(n => n.cities5g))
  ).sort((a, b) => a.localeCompare(b))

  const cityHas: Record<string, Set<string>> = {}
  for (const n of NETWORK_INFO) {
    for (const c of n.cities5g) {
      if (!cityHas[c]) cityHas[c] = new Set()
      cityHas[c].add(n.key)
    }
  }

  const q = filter.trim().toLowerCase()
  const visibleCities = q ? allCities.filter(c => c.toLowerCase().includes(q)) : allCities

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <h4 className="text-2xl font-bold text-gray-900 mb-1">4G &amp; 5G Coverage By Town</h4>
          <p className="text-sm text-gray-600">
            5G availability per UK town for each major network. 4G is available across
            &gt;99% of the UK population on all four networks.
          </p>
        </div>
        <input
          type="text"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder="Filter towns…"
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 w-full sm:w-56"
        />
      </div>

      <div className="overflow-x-auto rounded-2xl border border-gray-200 max-h-[28rem] overflow-y-auto">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-gray-100 text-gray-700 sticky top-0 z-10 shadow-sm">
            <tr>
              <th className="text-left px-4 py-3 font-semibold border-b border-gray-200 min-w-[14rem]">
                Town / City
              </th>
              {orderedNetworks.map(n => (
                <th
                  key={n.key}
                  className="px-3 py-3 font-semibold text-center border-b border-gray-200 whitespace-nowrap"
                >
                  {n.name} 5G Towns &amp; Cities
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleCities.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-sm text-gray-500">
                  No towns match "{filter}".
                </td>
              </tr>
            ) : (
              visibleCities.map((city, i) => {
                const rowBg = i % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                return (
                  <tr key={city} className={`${rowBg} hover:bg-primary-50/40`}>
                    <td className="px-4 py-2 font-medium text-gray-900 border-b border-gray-100">
                      {city}
                    </td>
                    {orderedNetworks.map(n => {
                      const has = cityHas[city]?.has(n.key)
                      return (
                        <td
                          key={n.key}
                          className="px-3 py-2 text-center border-b border-gray-100"
                        >
                          {has ? (
                            <CheckCircle className="w-4 h-4 text-green-600 inline" />
                          ) : (
                            <XCircle className="w-4 h-4 text-gray-300 inline" />
                          )}
                        </td>
                      )
                    })}
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-gray-500">
        Showing {visibleCities.length} of {allCities.length} towns &amp; cities. Source: 5g.co.uk
      </p>
    </div>
  )
}

function BandsPanel({ networkKey }: { networkKey: NetworkKey }) {
  const n = getNetwork(networkKey)
  return (
    <div>
      <h4 className="text-2xl font-bold text-gray-900 mb-4">{n.name} Frequency Bands</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5">
          <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">5G Bands</p>
          <div className="flex flex-wrap gap-2">
            {n.bands5g.map(b => (
              <span key={b} className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-mono">
                {b}
              </span>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5">
          <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">4G Bands</p>
          <div className="flex flex-wrap gap-2">
            {n.bands4g.map(b => (
              <span key={b} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-mono">
                {b}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

function StatusPanel() {
  const { isAuthenticated } = useAuthStore()
  return (
    <div>
      <h4 className="text-2xl font-bold text-gray-900 mb-2">Real-Time Network Status</h4>
      <p className="text-sm text-gray-600 mb-5">
        Run a live status check for your postcode against EE, O2, Three and Vodafone in the
        secure customer status checker. You must be logged in to view this.
      </p>

      {isAuthenticated ? (
        <Link
          to="/account/network-status"
          className="inline-flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-primary-600 to-pink-600 text-white rounded-xl font-semibold shadow hover:shadow-lg transition-all"
        >
          Open status checker
          <ArrowRight className="w-4 h-4" />
        </Link>
      ) : (
        <div className="rounded-2xl border border-yellow-200 bg-yellow-50 p-5">
          <div className="flex items-start gap-3 mb-3">
            <Lock className="w-5 h-5 text-yellow-700 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-yellow-900">
              You must be logged in to use the real-time status checker.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/login?redirect=/account/network-status"
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-semibold text-sm transition-all"
            >
              Log in
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 rounded-lg font-semibold text-sm transition-all"
            >
              Create an account
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}
